export const OPERATIONAL_COLLECTIONS = new Set(['services','occurrences','orders','inspections','vet','poi','pastures']);

export function isOperationalCollection(collectionName = '') {
  return OPERATIONAL_COLLECTIONS.has(String(collectionName || '').trim());
}

const DEFAULT_ARRAY_FIELDS = [
  'timeline',
  'materials',
  'assignees',
  'photos',
  'servicePhotos',
  'occurrencePhotos',
  'nfPhotos',
  'pauseHistory',
  'costCenters',
];

export function parseOperationalTs(value) {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function operationalRecordTs(item) {
  if (!item || typeof item !== 'object') return 0;
  return Math.max(
    parseOperationalTs(item?.deletedAt),
    parseOperationalTs(item?.deleted_at),
    parseOperationalTs(item?.updatedAt),
    parseOperationalTs(item?.createdAt)
  );
}

export function isOperationalRecordDeleted(item) {
  return !!(item && typeof item === 'object' && (item?._deleted === true || item?.deletedAt || item?.deleted_at));
}

function stableJson(value) {
  if (value === null || value === undefined) return String(value);
  try {
    return JSON.stringify(value, Object.keys(value || {}).sort());
  } catch {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
}

function mergeArrayField(currentValue, nextValue) {
  const current = Array.isArray(currentValue) ? currentValue : [];
  const next = Array.isArray(nextValue) ? nextValue : [];
  if (!current.length) return next.slice();
  if (!next.length) return current.slice();
  const merged = [];
  const seen = new Set();
  [...current, ...next].forEach((entry) => {
    if (entry === undefined) return;
    const key = stableJson(entry);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(entry);
  });
  return merged;
}

function mergeOperationalFields(current, next, arrayFields = DEFAULT_ARRAY_FIELDS) {
  const out = { ...current };
  Object.keys(next || {}).forEach((key) => {
    const nextValue = next?.[key];
    const currentValue = out?.[key];
    if (arrayFields.includes(key)) {
      out[key] = mergeArrayField(currentValue, nextValue);
      return;
    }
    if (nextValue === undefined || nextValue === null || nextValue === '') {
      if (currentValue !== undefined) return;
    }
    out[key] = nextValue;
  });
  return out;
}

export function mergeOperationalRecordVersions(records, options = {}) {
  const arrayFields = Array.isArray(options?.arrayFields) && options.arrayFields.length ? options.arrayFields : DEFAULT_ARRAY_FIELDS;
  const list = (Array.isArray(records) ? records : []).filter(item => item && typeof item === 'object').map(item => ({ ...item }));
  if (!list.length) return null;
  const sorted = list.slice().sort((a, b) => {
    const tsDiff = operationalRecordTs(a) - operationalRecordTs(b);
    if (tsDiff !== 0) return tsDiff;
    const aDeleted = isOperationalRecordDeleted(a) ? 1 : 0;
    const bDeleted = isOperationalRecordDeleted(b) ? 1 : 0;
    if (aDeleted !== bDeleted) return aDeleted - bDeleted;
    return stableJson(a).localeCompare(stableJson(b));
  });
  let merged = { ...sorted[0] };
  for (let index = 1; index < sorted.length; index += 1) {
    merged = mergeOperationalFields(merged, sorted[index], arrayFields);
  }
  const latest = sorted[sorted.length - 1];
  if (isOperationalRecordDeleted(latest)) {
    merged._deleted = true;
    merged.deletedAt = latest?.deletedAt || latest?.deleted_at || merged?.deletedAt || merged?.updatedAt || new Date().toISOString();
    merged.deleted_at = merged.deletedAt;
    if (latest?.deletedBy || merged?.deletedBy) merged.deletedBy = latest?.deletedBy || merged?.deletedBy || '';
  }
  return merged;
}

export function choosePreferredOperationalRecord(left, right, options = {}) {
  if (!left) return right;
  if (!right) return left;
  const leftTs = operationalRecordTs(left);
  const rightTs = operationalRecordTs(right);
  if (leftTs !== rightTs) {
    return mergeOperationalRecordVersions(leftTs < rightTs ? [left, right] : [right, left], options);
  }
  const leftDeleted = isOperationalRecordDeleted(left);
  const rightDeleted = isOperationalRecordDeleted(right);
  if (leftDeleted !== rightDeleted) {
    return mergeOperationalRecordVersions(leftDeleted ? [right, left] : [left, right], options);
  }
  return mergeOperationalRecordVersions([left, right], options);
}

export function stripDeletedOperationalRecords(items) {
  return (Array.isArray(items) ? items : []).filter(item => !isOperationalRecordDeleted(item));
}
